import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { View, type ViewStyle } from 'react-native';
import { twJoin } from 'tailwind-merge';

import type { RenderContext } from './RenderContext';
import { resolveMessagePlan } from './messagePlan';

import { SendStatusLine } from '@/components/SendStatusLine';
import { CustomBubbleProps, ICustomMessage } from '@/components/chat';
import { DefaultChatBubble } from '@/components/chat/bubbles/DefaultChatBubble';
import { MessageProcessingProgress } from '@/components/chat/bubbles/MessageProcessingProgress';
import { MessageBubbleWithLabel } from '@/components/chat/message-list/MessageBubbleWithLabel';
import { BUBBLE_ENTRIES } from '@/components/chat/message-types/bubbleEntries';
import { applyEntry, resolveEntry } from '@/components/chat/message-types/registry';
import { isAskAnonymouslyRoutedInquiry } from '@/util/inquiryDisplay';

/**
 * Builds the GiftedChat `renderBubble` callback.
 *
 * Which bubble renders comes from the registry — there is no per-type branch
 * here. What remains is row composition: the caption, the expert-relay chrome,
 * the layout container, and the two decorations that are not message types
 * (the add-references prompt and the processing indicator).
 */
export function makeRenderBubble(ctx: RenderContext) {
  return function renderBubble(props: CustomBubbleProps) {
    const currentMsg = props.currentMessage as ICustomMessage;

    const plan = resolveMessagePlan(currentMsg, ctx);

    const { isCurrentUserMessage, isExpertRelayMessage, messageCaption } = plan;

    const showMessageCaptionForViewer = plan.isCaptionViewer;

    const showExpertRelayChrome = !isCurrentUserMessage && isExpertRelayMessage;

    const shouldLeftAlignAskAnonymouslyCaption =
      showMessageCaptionForViewer && isAskAnonymouslyRoutedInquiry(ctx.inquiry);

    const isOutgoingCaptionWeb = isWeb && showMessageCaptionForViewer && isCurrentUserMessage;

    /** Web: shrink-wrap the slot and push it right; avoid NativeWind width utilities here so they don't override `style`. */
    const outgoingCaptionWebRowStyle: ViewStyle | undefined = isOutgoingCaptionWeb
      ? ({
          minWidth: 0,
          maxWidth: '100%',
          width: 'fit-content',
          marginLeft: 'auto',
          flexGrow: 0,
          flexShrink: 1,
          flexBasis: 'auto',
        } as unknown as ViewStyle)
      : undefined;

    /** A message not stored yet has no processing to show; its send status stands in for it. */
    const isPendingProcessing = plan.isPendingProcessing && !currentMsg.outboxStatus;

    const { outboxStatus, clientMessageId } = currentMsg;

    /** Narrow `'complete'` out for the progress bar — the predicate
     * above already filtered it, but TypeScript can't see across the
     * helper boundary. */
    const pendingStatus =
      currentMsg.processingStatus === 'complete' ? undefined : currentMsg.processingStatus;

    /**
     * The ordinary bubble, built here only so a variant that throws has
     * something to degrade to — `fallback: 'default'` resolves to it. The
     * terminal registry entry renders the same thing on the happy path.
     */
    const defaultBubble = (
      <DefaultChatBubble
        bubbleProps={props}
        colors={ctx.colors}
        closedConversation={ctx.closedConversation}
        isAuthor={ctx.isAuthor}
        conversationId={ctx.conversation.id}
        userId={ctx.userId}
        shrinkWidthToContent={showMessageCaptionForViewer}
      />
    );

    /**
     * The bubble this message renders. The terminal entry matches everything,
     * so the registry always resolves one — there is no per-type branch left
     * here. A caption hosts it in its slot; otherwise it stands on its own.
     */
    const resolveBubble = () => {
      const entry = resolveEntry(BUBBLE_ENTRIES, currentMsg, ctx);

      if (!entry) {
        return defaultBubble;
      }

      const resolution = applyEntry(entry, currentMsg, ctx, props, defaultBubble);

      if (resolution.kind === 'element') {
        return resolution.element;
      }

      // `fallthrough` — the entry claimed the message but its payload could not
      // paint the bubble, so the plain text renders instead.
      if (resolution.kind === 'fallthrough') {
        return defaultBubble;
      }

      return null;
    };

    const bubble = resolveBubble();

    return (
      <View
        className={twJoin(
          !isOutgoingCaptionWeb &&
            twJoin('min-w-0 max-w-full', isCurrentUserMessage ? 'self-end' : 'flex-1'),
          isWeb && showExpertRelayChrome && 'overflow-visible'
        )}
        style={outgoingCaptionWebRowStyle}
        {...(showExpertRelayChrome && !showMessageCaptionForViewer
          ? { accessibilityLabel: messageCaption }
          : {})}>
        {showMessageCaptionForViewer ? (
          <MessageBubbleWithLabel
            messageCaption={messageCaption}
            shouldLeftAlignAskAnonymouslyCaption={shouldLeftAlignAskAnonymouslyCaption}
            isCurrentUserMessage={isCurrentUserMessage}
            isOutgoingCaptionWeb={isOutgoingCaptionWeb}
            isExpertRelayMessage={isExpertRelayMessage}
            bubbleProps={props}
            colors={ctx.colors}
            closedConversation={ctx.closedConversation}
            isAuthor={ctx.isAuthor}
            conversationId={ctx.conversation.id}
            userId={ctx.userId}
            bubbleSlot={bubble}
          />
        ) : (
          bubble
        )}
        {outboxStatus && clientMessageId ? (
          <View className="self-end">
            <SendStatusLine
              status={outboxStatus}
              caption="Sending…"
              onRetry={() => ctx.onRetrySend(clientMessageId)}
              onDiscard={() => ctx.onDiscardSend(clientMessageId)}
            />
          </View>
        ) : null}
        {isPendingProcessing && (
          <MessageProcessingProgress
            messageSid={currentMsg.messageId}
            conversationId={ctx.conversation.id}
            status={pendingStatus}
          />
        )}
      </View>
    );
  };
}
